import {
  Roi,
  Coordinates,
  MosaicMapper,
  RoiFinding,
  RoiChange,
  ChangeProjectionService,
  CounterDeltas,
  type LabelCategory,
} from '@analysis/domain';
import { type RoiChangesRepository } from '../../ports';
import { AnalyzerEntity, UuidGenerator } from '@telespot/domain';

export interface CreateNewRoiPayload {
  coordinates: Coordinates;
  assetId: string;
  sampleId: string;
  roiFindings: RoiFinding[];
  selectedLabels: any[]; // TODO: specify type for this
  categories: Record<string, LabelCategory>;
  creatorId: string;
  creatorEntity: AnalyzerEntity;
  additionalLabelsIncluded?: boolean;
}

export interface CreateNewRoiResult {
  roiFindings: RoiFinding[];
  updatedRois: Map<string, Roi>;
  changes: Map<string, RoiChange>;
  counterDeltas: CounterDeltas;
}

export class CreateNewRoiUseCase {
  constructor(
    private readonly roiChanges: RoiChangesRepository,
    private readonly uuids: UuidGenerator
  ) {}

  public async execute(payload: CreateNewRoiPayload): Promise<CreateNewRoiResult> {
    const selectedLabels = payload.selectedLabels;
    const stepIds = new Set<string>();
    const labelsMap = new Map<string, number>();

    if (selectedLabels.length > 0) {
      selectedLabels.forEach((label) => {
        labelsMap.set(label.id, 1);
        if (label.stepId) {
          stepIds.add(label.stepId);
        }
      });
    }

    const existingRoiFindingStepIds = new Set(payload.roiFindings.map((f) => f.pipelineStepId));
    const missingStepIds = Array.from(stepIds).filter((stepId) => !existingRoiFindingStepIds.has(stepId));

    // Create new ROI findings for missing stepIds
    let newRoiFindings: RoiFinding[] = [];
    let updatedRoiFindings = [...payload.roiFindings];

    if (missingStepIds.length > 0) {
      newRoiFindings = missingStepIds.map((stepId) => {
        const correspondingLabel = selectedLabels.find((label) => label.stepId === stepId);
        const pipelineId = correspondingLabel?.pipelineId || '';

        const findingId = `new:${this.uuids.generate()}`;
        const analysisId = undefined;

        const matchingCategory = Object.values(payload.categories).find((category) => category.stepId === stepId);
        const findingType = matchingCategory?.stepTask;

        const roiFinding: RoiFinding = {
          findingType,
          assetId: payload.assetId,
          sampleId: payload.sampleId,
          creatorId: payload.creatorId,
          creatorEntity: payload.creatorEntity,
          findingId: this.uuids.generate(),
          analysisId,
          pipelineStepId: stepId,
          pipelineId: pipelineId || '',
          findingVersion: 1,
          findingVersionId: findingId,
          originFindingVersionId: undefined,
        };

        return roiFinding;
      });

      updatedRoiFindings = [...payload.roiFindings, ...newRoiFindings];
    }

    const newRois: Map<string, Roi> = new Map();
    const changes = new Map<string, RoiChange>();

    for (const finding of updatedRoiFindings) {
      const roiId = MosaicMapper.createSpatialKey({ ...payload.coordinates, findingId: finding.findingId });

      const roi: Roi = {
        id: roiId,
        uuid: undefined,
        assetId: payload.assetId,
        sampleId: payload.sampleId,
        analysisId: finding.analysisId,
        findingId: finding.findingId,
        findingVersion: finding.findingVersion,
        findingVersionId: finding.findingVersionId,
        coordinates: payload.coordinates,
        labels: labelsMap,
        creatorId: payload.creatorId,
        additionalLabelsIncluded: payload.additionalLabelsIncluded ?? true,
        pipelineStepId: finding.pipelineStepId,
        pipelineId: finding.pipelineId,
        findingType: finding.findingType,
        creatorEntity: finding.creatorEntity,
      };

      newRois.set(roiId, roi);

      changes.set(roi.id, {
        id: roi.id,
        sampleId: roi.sampleId,
        assetId: roi.assetId,
        creatorId: roi.creatorId,
        timestamp: Date.now(),
        labelsAdded: Array.from(labelsMap.keys()),
        labelsRemoved: [],
        isNew: true,
        snapshot: roi,
      });
    }

    await this.roiChanges.upsertRoiChanges(changes);

    const perRoiDeltas: CounterDeltas[] = [];
    for (const roi of newRois.values()) {
      const currentLabels = Array.from(roi.labels.keys());
      perRoiDeltas.push(ChangeProjectionService.computeCounterDeltas([], currentLabels, currentLabels, []));
    }
    const counterDeltas = ChangeProjectionService.mergeCounterDeltas(perRoiDeltas);

    return {
      roiFindings: newRoiFindings,
      updatedRois: newRois,
      changes,
      counterDeltas,
    };
  }
}
